import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { useGHL } from 'Context/GHL';

import {
  searchOpportunities,
  listTemplates,
  getPrefill,
  listSubmissions,
  createSubmission,
  sendForSignature,
} from './actions';
import type { OpportunityOption, PdfFormTemplate, PdfFormSubmission } from './actions';

import classes from './ghlDocuments.module.css';

// GHL Documents — PDF forms / e-signature for the GHL embed (MONGOOSE-FR-009).
// Flow: pick an opportunity → list its project's submissions, create a new one
// from a template (prefilled from the opportunity), and send it for signature.
function GHLDocumentsContainer() {
  const { isAuthenticated, ssoToken, error: ghlError } = useGHL();

  // -- opportunity selection --
  const [oppSearch, setOppSearch] = useState('');
  const [oppResults, setOppResults] = useState<OpportunityOption[]>([]);
  const [oppDropdownOpen, setOppDropdownOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState<OpportunityOption | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // -- documents for the selected opportunity --
  const [templates, setTemplates] = useState<PdfFormTemplate[]>([]);
  const [submissions, setSubmissions] = useState<PdfFormSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // -- new submission form --
  const [templateId, setTemplateId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');

  // -- per-row send state --
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [notice, setNotice] = useState('');

  // Debounced opportunity search.
  const onOppSearchChange = useCallback(
    (value: string) => {
      setOppSearch(value);
      setOppDropdownOpen(true);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(async () => {
        if (!ssoToken) return;
        try {
          setOppResults(await searchOpportunities(ssoToken, value));
        } catch {
          setOppResults([]);
        }
      }, 300);
    },
    [ssoToken]
  );

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  // Load an initial list of recent opportunities so the picker isn't empty
  // before the user types.
  useEffect(() => {
    if (!ssoToken) return undefined;
    let cancelled = false;
    searchOpportunities(ssoToken, '')
      .then((opps) => {
        if (!cancelled) setOppResults(opps);
      })
      .catch(() => {
        if (!cancelled) setOppResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ssoToken]);

  // Load templates + submissions + prefill once an opportunity is chosen.
  const loadForOpportunity = useCallback(
    async (opp: OpportunityOption) => {
      if (!ssoToken) return;
      setLoading(true);
      setLoadError('');
      setNotice('');
      setTemplateId('');
      setFormError('');
      try {
        const [tpls, subs, prefill] = await Promise.all([
          listTemplates(ssoToken),
          listSubmissions(ssoToken, opp.id),
          getPrefill(ssoToken, opp.id).catch(() => null),
        ]);
        setTemplates(tpls);
        setSubmissions(subs);
        setClientName(prefill?.clientName || opp.contactName || '');
        setClientEmail(prefill?.clientEmail || opp.contactEmail || '');
        setClientPhone(prefill?.clientPhone || opp.contactPhone || '');
      } catch (err: any) {
        // The most common case: opportunity has no linked project yet (422).
        setLoadError(
          err?.response?.data?.message ||
            'Unable to load documents for this opportunity.'
        );
        setTemplates([]);
        setSubmissions([]);
      } finally {
        setLoading(false);
      }
    },
    [ssoToken]
  );

  const onSelectOpp = useCallback(
    (opp: OpportunityOption) => {
      setSelectedOpp(opp);
      setOppSearch(opp.name);
      setOppDropdownOpen(false);
      loadForOpportunity(opp);
    },
    [loadForOpportunity]
  );

  const onClearOpp = useCallback(() => {
    setSelectedOpp(null);
    setOppSearch('');
    setOppResults([]);
    setTemplates([]);
    setSubmissions([]);
    setLoadError('');
    setNotice('');
  }, []);

  const onCreate = useCallback(async () => {
    if (!ssoToken || !selectedOpp || !templateId || !clientName.trim()) return;
    setCreating(true);
    setFormError('');
    setNotice('');
    try {
      const submission = await createSubmission(ssoToken, selectedOpp.id, {
        pdfFormTemplateId: Number(templateId),
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim() || null,
        clientPhone: clientPhone.trim() || null,
      });
      setSubmissions((prev) => [submission, ...prev]);
      setTemplateId('');
      setNotice('Document created. You can now send it for signature.');
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Failed to create document.');
    } finally {
      setCreating(false);
    }
  }, [ssoToken, selectedOpp, templateId, clientName, clientEmail, clientPhone]);

  const onSend = useCallback(
    async (submission: PdfFormSubmission) => {
      if (!ssoToken) return;
      setSendingId(submission.id);
      setNotice('');
      setLoadError('');
      try {
        const message = await sendForSignature(ssoToken, submission.id, {
          email: submission.clientEmail,
          phone: submission.clientPhone,
        });
        setNotice(message);
        // Reflect the sent timestamps without a full refetch.
        setSubmissions((prev) =>
          prev.map((s) =>
            (s.id === submission.id
              ? {
                  ...s,
                  emailedAt: s.clientEmail ? new Date().toISOString() : s.emailedAt,
                  smsSentAt: s.clientPhone ? new Date().toISOString() : s.smsSentAt,
                }
              : s)
          )
        );
      } catch (err: any) {
        setLoadError(err?.response?.data?.message || 'Failed to send for signature.');
      } finally {
        setSendingId(null);
      }
    },
    [ssoToken]
  );

  if (ghlError) {
    return (
      <div className={classes.container}>
        <div className={classes.errorCard}>
          <h2>Connection Error</h2>
          <p>{ghlError}</p>
          <p className={classes.helpText}>
            Please ensure this page is accessed from within RocketCRM.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={classes.container}>
        <div className={classes.card}>
          <h2>Not Authenticated</h2>
          <p>Please access this page from within RocketCRM.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1>PDF Forms</h1>
        <p className={classes.subtitle}>
          Select an opportunity to create and send PDF forms for signature.
        </p>
      </div>

      {/* Opportunity picker */}
      <div className={classes.card}>
        <label className={classes.fieldLabel}>Opportunity</label>
        {selectedOpp ? (
          <div className={classes.selectedRow}>
            <div>
              <strong>{selectedOpp.name}</strong>
              {selectedOpp.contactName && (
                <span className={classes.muted}>{` — ${selectedOpp.contactName}`}</span>
              )}
            </div>
            <button type="button" className={classes.linkBtn} onClick={onClearOpp}>
              Change
            </button>
          </div>
        ) : (
          <div className={classes.searchWrap}>
            <input
              type="text"
              value={oppSearch}
              onChange={(e) => onOppSearchChange(e.target.value)}
              onFocus={() => setOppDropdownOpen(true)}
              onBlur={() => setOppDropdownOpen(false)}
              placeholder="Search opportunities..."
              className={classes.input}
            />
            {oppDropdownOpen && (
              <div className={classes.dropdown}>
                {oppResults.length > 0 ? (
                  oppResults.map((o) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div
                      key={o.id}
                      className={classes.dropdownItem}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onSelectOpp(o);
                      }}
                    >
                      <strong>{o.name}</strong>
                      {o.contactName && (
                        <span className={classes.muted}>{` — ${o.contactName}`}</span>
                      )}
                      {o.stageName && <span className={classes.badge}>{o.stageName}</span>}
                    </div>
                  ))
                ) : (
                  <div className={classes.dropdownEmpty}>No opportunities found</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {notice && <div className={classes.notice}>{notice}</div>}
      {loadError && <div className={classes.errorBanner}>{loadError}</div>}

      {selectedOpp && loading && (
        <div className={classes.card}>
          <p>Loading documents…</p>
        </div>
      )}

      {selectedOpp && !loading && !loadError && (
        <>
          {/* New document */}
          <div className={classes.card}>
            <h3 className={classes.cardTitle}>New Document</h3>
            {templates.length === 0 ? (
              <p className={classes.helpText}>
                No active PDF form templates for this company. Create one in the
                document builder first.
              </p>
            ) : (
              <div className={classes.formGrid}>
                <div className={classes.formGroup}>
                  <label className={classes.fieldLabel}>Template</label>
                  <select
                    className={classes.input}
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                  >
                    <option value="">Select a template…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={classes.formGroup}>
                  <label className={classes.fieldLabel}>Client Name</label>
                  <input
                    type="text"
                    className={classes.input}
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Client name"
                  />
                </div>
                <div className={classes.formGroup}>
                  <label className={classes.fieldLabel}>Client Email</label>
                  <input
                    type="email"
                    className={classes.input}
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="client@example.com"
                  />
                </div>
                <div className={classes.formGroup}>
                  <label className={classes.fieldLabel}>Client Phone</label>
                  <input
                    type="tel"
                    className={classes.input}
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="+1 555 555 5555"
                  />
                </div>
              </div>
            )}
            {formError && <p className={classes.errorText}>{formError}</p>}
            {templates.length > 0 && (
              <button
                type="button"
                className={classes.primaryBtn}
                onClick={onCreate}
                disabled={!templateId || !clientName.trim() || creating}
              >
                {creating ? 'Creating…' : 'Create Document'}
              </button>
            )}
          </div>

          {/* Existing submissions */}
          <div className={classes.card}>
            <h3 className={classes.cardTitle}>Documents</h3>
            {submissions.length === 0 ? (
              <p className={classes.helpText}>No documents yet for this opportunity.</p>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th>Form</th>
                      <th>Client</th>
                      <th>Status</th>
                      <th>Sent</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((s) => {
                      const sent = [s.emailedAt && 'Email', s.smsSentAt && 'SMS']
                        .filter(Boolean)
                        .join(', ');
                      const canSend = Boolean(s.clientEmail || s.clientPhone);
                      return (
                        <tr key={s.id}>
                          <td>{s.templateName || '—'}</td>
                          <td>
                            {s.clientName}
                            {s.clientEmail && (
                              <div className={classes.muted}>{s.clientEmail}</div>
                            )}
                          </td>
                          <td>
                            <span className={classes.statusBadge}>{s.status}</span>
                          </td>
                          <td>{sent || '—'}</td>
                          <td className={classes.actionsCell}>
                            {s.prefilledUrl && (
                              <a
                                href={s.prefilledUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={classes.linkBtn}
                              >
                                Preview
                              </a>
                            )}
                            {s.signedUrl && (
                              <a
                                href={s.signedUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={classes.linkBtn}
                              >
                                Signed
                              </a>
                            )}
                            {s.status !== 'signed' && (
                              <button
                                type="button"
                                className={classes.secondaryBtn}
                                onClick={() => onSend(s)}
                                disabled={!canSend || sendingId === s.id}
                                title={
                                  canSend
                                    ? 'Send signing link to the client'
                                    : 'Add a client email or phone first'
                                }
                              >
                                {sendingId === s.id ? 'Sending…' : 'Send'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const GHLDocumentsContainerMemo = memo(GHLDocumentsContainer, areEqual);

export { GHLDocumentsContainerMemo as GHLDocumentsContainer };
